import { memo, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import {
  ErrorAlertIcon as ErrorIcon,
  SupportStrokeIcon,
} from "../../../assets/icons";
import {
  DEFERRED_ORIGINATION_ROUTES,
  FUND_REQUEST_ROUTES,
  SEND_ROUTES,
} from "../../../constants";
import { useStore } from "../../../store/globalState";
import { useSupportSheet } from "../../../components/SupportSheet";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";
import { Navbar } from "../../../components/Navbar";

type CancelFailedContext = {
  namespace: string;
  returnTo: string;
};

const resolveContext = (pathname: string): CancelFailedContext => {
  if (pathname.startsWith("/send/")) {
    return {
      namespace: "send",
      returnTo: `${SEND_ROUTES.RECEIPT}/op_gallery_send_processing`,
    };
  }
  if (pathname.startsWith("/deferred-origination/")) {
    return {
      namespace: "deferred-origination",
      returnTo: `${DEFERRED_ORIGINATION_ROUTES.RECEIPT}/dpr-gallery-pending`,
    };
  }
  return {
    namespace: "fund-request",
    returnTo: `${FUND_REQUEST_ROUTES.RECEIPT}/op_gallery_processing`,
  };
};

/**
 * Gallery screen for cancel-transfer failure (operation no longer cancellable).
 * Mirrors Cancellation In Progress: need-help header + Regresar to receipt.
 */
export const CancellationFailedPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { namespace, returnTo } = useMemo(
    () => resolveContext(pathname),
    [pathname],
  );
  const { openSupport, supportSheet } = useSupportSheet(namespace);

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header flow-landing-page">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            noBackButton
            color={title}
            onIconClick={openSupport}
            icon={
              <SupportStrokeIcon
                size={35}
                colors={[backgroundIcon, backgroundIcon]}
                glyphColor={iconColors[0]}
              />
            }
          />
        </Layout.Header>
        <Layout.Content className="landing-section flow-hero-section flow-hero-section--with-header">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <ErrorIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("error.cancellationFailedTitle")}
                highlightColor={title}
                secudnary={t("error.cancellationFailedSubtitle")}
                secudnaryColor={title}
              />
              <BodyCopy textColor={bodyCopy}>
                {t("error.cancellationFailedMessage")}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => navigate(returnTo)}
            >
              {t("error.returnToTransactions")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
